import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Text,
  ModalBody,
  Button,
  Box,
  Heading,
  ModalFooter,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { Icons } from '../Icons';
import { SharedDisclosureProps } from '../types';
import { useEffect, useState } from 'react';
import { useActiveSession } from '@texas/hooks/useSession';
import {
  GeneratedProductApproval,
  ProductApproval,
  productApprovalsApi,
} from '@texas/api/endpoints/productApprovalsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useTranslation } from 'react-i18next';
import {
  ContactAddToType,
  SimpleContact,
} from '@texas/api/endpoints/contactsApi';
import { ContactsModal } from '@texas/components/contacts/ContactsModal';
import { EmptyDataButton } from '../button/EmptyDataButton';
import { WarningComponent } from '../WarningComponent';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '../alert/ErrorDetails';
import { productApprovalEvents } from './events';
import { SpinnerWithLabel } from '../SpinnerWithLabel';
import { UserAvatar } from '../users/UserAvatar';
import { SimpleUserAvatarButtons } from '../users/SimpleUserAvatarButtons';
import { TexasDatePicker } from '../datePicker/TexasDatePicker';

enum CreateProductApprovalState {
  Accepted = 1,
  Ignored = 2,
}

interface CreateProductApproval {
  customerContact: SimpleContact | null;
  supplierContact: SimpleContact | null;
  supplierNote: string;
  issuedBy: SimpleContact;
  state: CreateProductApprovalState;
  variantId: number;
  supplierId: number;
  branchId: number;
  matrixNodeXId: number | null;
  matrixNodeYId: number | null;
  compositionGroupId: number;
  externalSupplierItemCode: string;
  sampleReadyDate: Date;
}

interface Props extends SharedDisclosureProps {
  groupIds: number[];
  onSubmit?: () => void;
}

export function GeneratePaFromGroupsModal(props: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="4xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex gap={2} align="center">
            <Text>{t('productApproval.createNew')}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <Inner
          groupIds={props.groupIds}
          onSubmit={() => {
            productApprovalEvents.productApprovalsCreated.dispatch();
            if (props.onSubmit) props.onSubmit();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  groupIds,
  onSubmit,
}: {
  groupIds: number[];
  onSubmit: (pa: ProductApproval[]) => void;
}) {
  const { t } = useTranslation();
  const { data, refetch, loading } = useApiResource(
    productApprovalsApi.generate,
  );
  const { currentUser } = useActiveSession();
  const [generatedProductApprovals, setGeneratedProductApprovals] = useState<
    Record<number, CreateProductApproval>
  >({});

  const {
    request: createPaRequest,
    loading: createPaLoading,
    error: createPaError,
  } = useApiRequest(productApprovalsApi.create);

  const performRequest = async () => {
    const data = Object.values(generatedProductApprovals);
    await request(
      createPaRequest,
      [
        {
          data: data
            .filter((d) => d.state === CreateProductApprovalState.Accepted)
            .map((d) => ({
              branchId: d.branchId,
              compositionGroupId: d.compositionGroupId,
              customerContactId: d.customerContact!.id,
              supplierContactId: d.supplierContact!.id,
              externalSupplierItemCode: d.externalSupplierItemCode,
              matrixNodeXId: d.matrixNodeXId,
              matrixNodeYId: d.matrixNodeYId,
              supplierId: d.supplierId,
              supplierNote: d.supplierNote,
              variantId: d.variantId,
              sampleReadyDate: d.sampleReadyDate,
            })),
        },
      ],
      (pas) => {
        onSubmit(pas);
      },
    );
  };

  useEffect(() => {
    refetch(groupIds);
  }, [refetch, groupIds]);

  const {
    value: supplierContactValue,
    isOpen: isSupplierAddContactOpen,
    onClose: onSupplierAddContactClose,
    onOpen: onSupplierAddContactOpen,
  } = useValueDisclosure<{ supplierId: number; compositionGroupId: number }>();
  const {
    value: customerContactValue,
    isOpen: isCustomerAddContactOpen,
    onClose: onCustomerAddContactClose,
    onOpen: onCustomerAddContactOpen,
  } = useValueDisclosure<{ customerId: number; compositionGroupId: number }>();

  useEffect(() => {
    if (!data) return;

    const t: Record<number, CreateProductApproval> = {};
    data.forEach((s) => {
      t[s.compositionGroupId] = {
        customerContact: s.customerContact,
        issuedBy: {
          id: currentUser.id,
          name: currentUser.name,
          email: currentUser.email,
        },
        state: CreateProductApprovalState.Accepted,
        supplierContact: s.supplierContact,
        externalSupplierItemCode: '',
        supplierNote: '',
        branchId: s.branchId,
        matrixNodeXId: s.matrixNodeXId,
        matrixNodeYId: s.matrixNodeYId,
        variantId: s.variantId,
        supplierId: s.supplierId,
        compositionGroupId: s.compositionGroupId,
        sampleReadyDate: new Date(s.expectedReadyDate),
      };
    });

    setGeneratedProductApprovals(t);
  }, [data, currentUser.id, currentUser.name, currentUser.email]);

  return (
    <>
      {createPaError && <ErrorDetails error={createPaError} />}
      <ModalBody justifyContent="center">
        {loading && <SpinnerWithLabel label={t('general.loadingData')} />}
        <Flex gap={2} flexDirection="column">
          {data?.map((x) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (generatedProductApprovals[x.compositionGroupId] === undefined) {
              return null;
            }
            return (
              <PaItem
                key={x.compositionGroupId}
                pa={generatedProductApprovals[x.compositionGroupId]}
                sample={x}
                onCustomerContactClick={() =>
                  onCustomerAddContactOpen({
                    customerId: x.customerId,
                    compositionGroupId: x.compositionGroupId,
                  })
                }
                onSupplierContactClick={() =>
                  onSupplierAddContactOpen({
                    supplierId: x.supplierId,
                    compositionGroupId: x.compositionGroupId,
                  })
                }
                onPaIgnore={() => {
                  const toSet =
                    generatedProductApprovals[x.compositionGroupId].state ===
                    CreateProductApprovalState.Ignored
                      ? CreateProductApprovalState.Accepted
                      : CreateProductApprovalState.Ignored;
                  setGeneratedProductApprovals((s) => ({
                    ...s,
                    [x.compositionGroupId]: {
                      ...s[x.compositionGroupId],
                      state: toSet,
                    },
                  }));
                }}
                onSampleReadyDateChange={(date) => {
                  setGeneratedProductApprovals((s) => ({
                    ...s,
                    [x.compositionGroupId]: {
                      ...s[x.compositionGroupId],
                      sampleReadyDate: date,
                    },
                  }));
                }}
              />
            );
          })}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={performRequest}
          isLoading={createPaLoading}
          variant="texas-solid"
          isDisabled={!CanCreate(Object.values(generatedProductApprovals))}
        >
          {t('productApproval.createNew')}
        </Button>
      </ModalFooter>
      {supplierContactValue && (
        <ContactsModal
          mode="select"
          filterOn={ContactAddToType.Supplier}
          addTo={ContactAddToType.Supplier}
          referenceId={supplierContactValue.supplierId}
          onClose={() => {
            onSupplierAddContactClose();
          }}
          isOpen={isSupplierAddContactOpen}
          onCreatedAndConnected={(c) => {
            onSupplierAddContactClose();
            setGeneratedProductApprovals((s) => ({
              ...s,
              [supplierContactValue.compositionGroupId]: {
                ...s[supplierContactValue.compositionGroupId],
                supplierContact: c,
              },
            }));
          }}
          onSelect={(c) => {
            onSupplierAddContactClose();
            setGeneratedProductApprovals((s) => ({
              ...s,
              [supplierContactValue.compositionGroupId]: {
                ...s[supplierContactValue.compositionGroupId],
                supplierContact: c,
              },
            }));
          }}
        />
      )}
      {customerContactValue && (
        <ContactsModal
          mode="select"
          filterOn={ContactAddToType.Customer}
          addTo={ContactAddToType.Customer}
          referenceId={customerContactValue.customerId}
          onClose={() => {
            onCustomerAddContactClose();
          }}
          isOpen={isCustomerAddContactOpen}
          onCreatedAndConnected={(c) => {
            onCustomerAddContactClose();
            setGeneratedProductApprovals((s) => ({
              ...s,
              [customerContactValue.compositionGroupId]: {
                ...s[customerContactValue.compositionGroupId],
                customerContact: c,
              },
            }));
          }}
          onSelect={(c) => {
            onCustomerAddContactClose();
            setGeneratedProductApprovals((s) => ({
              ...s,
              [customerContactValue.compositionGroupId]: {
                ...s[customerContactValue.compositionGroupId],
                customerContact: c,
              },
            }));
          }}
        />
      )}
    </>
  );
}

function CanCreate(pas: CreateProductApproval[]) {
  const anyPaAccepted =
    pas.filter((x) => x.state === CreateProductApprovalState.Accepted).length >
    0;
  const anyInvalidPa = pas
    .filter((x) => x.state === CreateProductApprovalState.Accepted)
    .some((x) => x.customerContact === null || x.supplierContact === null);

  return !anyInvalidPa && anyPaAccepted;
}

function IsValid(pa: CreateProductApproval) {
  return pa.customerContact !== null && pa.supplierContact !== null;
}

function PaItem({
  pa,
  sample,
  onSupplierContactClick,
  onCustomerContactClick,
  onPaIgnore,
  onSampleReadyDateChange,
}: {
  pa: CreateProductApproval;
  sample: GeneratedProductApproval;
  onSupplierContactClick: () => void;
  onCustomerContactClick: () => void;
  onPaIgnore: () => void;
  onSampleReadyDateChange: (date: Date) => void;
}) {
  const { t } = useTranslation();

  const valid = IsValid(pa);

  return (
    <Box
      bg="texas.bg.700"
      borderRadius="md"
      boxShadow="md"
      transition="transform 200ms ease, opacity 50ms ease"
      border="1px solid"
      _light={{
        bg: 'gray.100',
      }}
      {...(valid
        ? { borderColor: 'transparent' }
        : { borderColor: 'yellow.300' })}
      {...(pa.state === CreateProductApprovalState.Ignored
        ? { opacity: 0.6, transform: 'scale(0.96)', borderColor: 'transparent' }
        : null)}
    >
      <StateComponent onClick={onPaIgnore} state={pa.state}>
        <Flex
          flexDir="column"
          {...(pa.state === CreateProductApprovalState.Ignored
            ? { pointerEvents: 'none' }
            : null)}
        >
          <Flex py={2} alignItems="flex-start" px={4} gap={6}>
            <Flex flexDir="column">
              <Heading fontSize="md">{sample.articleItemCode}</Heading>
              <Text fontSize="sm" variant="sub">
                {sample.articleName}
              </Text>
            </Flex>
            <Box>
              <Text fontSize="sm">{sample.matrixNodeXValue}</Text>
              <Text fontSize="sm">{sample.matrixNodeYValue}</Text>
            </Box>
            <Box>
              <Text fontSize="sm">
                {t('compositionGroup.option.optionNr', { nr: sample.option })}
              </Text>
              <Text fontSize="sm" variant="sub">
                {t('compositionGroup.version.versionNr', {
                  nr: sample.version,
                })}
              </Text>
            </Box>
            <Flex flexGrow={1} justify="flex-end" pr={2}>
              <Heading fontSize="md">{sample.branchName}</Heading>
            </Flex>
          </Flex>
          <Flex
            justify="space-between"
            gap={8}
            py={2}
            px={4}
            bg="texas.bg.800"
            borderBottomRadius="md"
            _light={{ bg: 'gray.50' }}
          >
            <Box w="33%">
              {pa.supplierContact === null ? (
                <Flex direction="column" gap={1}>
                  <Text variant="sub" fontSize="sm">
                    {t('suppliers.supplierContact')}
                  </Text>
                  <Box maxH={32}>
                    <WarningComponent
                      warning={t('productApproval.supplierContactRequired')}
                    >
                      <EmptyDataButton
                        label={t('productApproval.selectSupplierContact')}
                        description={t('general.clickToSelect')}
                        onClick={onSupplierContactClick}
                      />
                    </WarningComponent>
                  </Box>
                </Flex>
              ) : (
                <Flex direction="column" gap={1}>
                  <Text variant="sub" fontSize="sm">
                    {t('suppliers.supplierContact')}
                  </Text>
                  <UserAvatar
                    name={pa.supplierContact.name}
                    email={pa.supplierContact.email}
                    label={sample.supplierName}
                    showTeams={false}
                    buttons={
                      <SimpleUserAvatarButtons
                        buttons={[
                          {
                            tooltip: t('contacts.change'),
                            icon: <Icons.SwapHorizontal />,
                            onClick: onSupplierContactClick,
                          },
                        ]}
                      />
                    }
                  />
                </Flex>
              )}
            </Box>
            <Box w="33%">
              {pa.customerContact === null ? (
                <Flex direction="column" gap={1}>
                  <Text variant="sub" fontSize="sm">
                    {t('customer.customerContact')}
                  </Text>
                  <Box maxH={32}>
                    <WarningComponent
                      warning={t('productApproval.brandContactRequired')}
                    >
                      <EmptyDataButton
                        label={t('productApproval.selectBrandContact')}
                        description={t('general.clickToSelect')}
                        onClick={onCustomerContactClick}
                      />
                    </WarningComponent>
                  </Box>
                </Flex>
              ) : (
                <Flex direction="column" gap={1}>
                  <Text variant="sub" fontSize="sm">
                    {t('customer.customerContact')}
                  </Text>
                  <UserAvatar
                    name={pa.customerContact.name}
                    email={pa.customerContact.email}
                    label={sample.customerName}
                    showTeams={false}
                    buttons={
                      <SimpleUserAvatarButtons
                        buttons={[
                          {
                            tooltip: t('contacts.change'),
                            icon: <Icons.SwapHorizontal />,
                            onClick: onCustomerContactClick,
                          },
                        ]}
                      />
                    }
                  />
                </Flex>
              )}
            </Box>
            <Box>
              <Flex direction="column" gap={1}>
                <Text variant="sub" fontSize="sm">
                  {t('productApproval.expectedReadyDate')}
                </Text>
                <TexasDatePicker
                  selectedDate={pa.sampleReadyDate}
                  onDateChange={(date) =>
                    date !== null ? onSampleReadyDateChange(date) : null
                  }
                  isClearable={false}
                />
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </StateComponent>
    </Box>
  );
}

function StateComponent({
  onClick,
  state,
  children,
}: {
  onClick: () => void;
  state: CreateProductApprovalState;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Box pos="relative" role="group">
      {children}
      <Box
        pos="absolute"
        right={-2}
        top={-3}
        transition="transform 200ms ease, opacity 200ms ease"
        opacity={0}
        transform="scale(0.9)"
        _groupHover={{ opacity: 1, transform: 'scale(1)' }}
      >
        {state === CreateProductApprovalState.Ignored ? (
          <>
            <Tooltip label={t('general.add')}>
              <IconButton
                colorScheme="green"
                variant="solid"
                size="sm"
                onClick={onClick}
                aria-label={t('general.add')}
                icon={<Icons.Plus />}
              />
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip label={t('wizard.productApproval.ignore')}>
              <IconButton
                colorScheme="red"
                variant="solid"
                size="sm"
                onClick={onClick}
                aria-label={t('general.remove')}
                icon={<Icons.Close />}
              />
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}
